import React from "react";
import { motion } from "framer-motion";
import {
  Database,
  FileText,
  Users,
  DollarSign,
  Calendar,
  Image,
  ClipboardList,
  Sparkles,
  BrainCircuit,
  ArrowRight,
  CheckCircle2,
  Shield,
  Zap,
  GitMerge,
} from "lucide-react";

const sourceDataItems = [
  { icon: Users, label: "Patients", color: "text-blue-400" },
  { icon: FileText, label: "Claims", color: "text-indigo-400" },
  { icon: DollarSign, label: "Ledgers", color: "text-emerald-400" },
  { icon: Calendar, label: "Appointments", color: "text-violet-400" },
  { icon: ClipboardList, label: "Treatment Plans", color: "text-cyan-400" },
  { icon: Image, label: "X-Rays & Images", color: "text-amber-400" },
  { icon: FileText, label: "Insurance Info", color: "text-rose-400" },
  { icon: Database, label: "Clinical Notes", color: "text-teal-400" },
];

const validationChecks = [
  "Record counts verified",
  "Balances reconciled",
  "Relationships intact",
  "Zero data loss confirmed",
  "Anomalies flagged & resolved",
  "Ready for go-live",
];

const DataMigrationSlide = () => {
  return (
    <div className="h-full w-full bg-[#0B1120] text-white flex flex-col px-8 py-6 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-600/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-indigo-600/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] pointer-events-none" />

      {/* Header */}
      <div className="text-center mb-6 flex-shrink-0 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-sm font-semibold mb-2 border border-blue-500/20">
            <GitMerge className="w-4 h-4" />
            <span>Seamless Data Migration</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-1">
            Your Data, Migrated{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
              Flawlessly
            </span>
          </h2>
          <p className="text-sm text-slate-400 max-w-2xl mx-auto">
            AI-powered extraction, intelligent mapping, and row-level validation — every record transferred perfectly.
          </p>
        </motion.div>
      </div>

      {/* Main animated graphic */}
      <div className="flex-1 flex items-center justify-center relative z-10 min-h-0">
        <div className="w-full max-w-[1300px] flex items-stretch gap-0">

          {/* SOURCE: Old PMS Data */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="w-[28%] flex flex-col"
          >
            <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-5 h-full flex flex-col">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center">
                  <Database className="w-4 h-4 text-slate-300" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">Your Current PMS</h3>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Source Data</p>
                </div>
              </div>

              <div className="space-y-1.5 flex-1">
                {sourceDataItems.map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.06 }}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-slate-700/40 border border-slate-600/30"
                  >
                    <item.icon className={`w-3.5 h-3.5 ${item.color} shrink-0`} />
                    <span className="text-xs font-medium text-slate-300">{item.label}</span>

                    {/* Streaming dots animation */}
                    <div className="ml-auto flex gap-0.5">
                      {[0, 1, 2].map((d) => (
                        <motion.div
                          key={d}
                          className="w-1 h-1 rounded-full bg-blue-400/60"
                          animate={{ opacity: [0.2, 1, 0.2] }}
                          transition={{
                            duration: 1.2,
                            repeat: Infinity,
                            delay: i * 0.15 + d * 0.2,
                          }}
                        />
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-center gap-2">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Dentrix, Eaglesoft, Open Dental, Curve...
                </span>
              </div>
            </div>
          </motion.div>

          {/* FLOW ARROWS: Source → AI */}
          <div className="w-[5%] flex flex-col items-center justify-center gap-2 relative">
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                className="flex items-center"
                animate={{ x: [0, 8, 0], opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  delay: i * 0.3,
                }}
              >
                <ArrowRight className="w-4 h-4 text-blue-400/70" />
              </motion.div>
            ))}
          </div>

          {/* CENTER: AI Transformation Engine */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="w-[34%] flex flex-col"
          >
            <div className="relative h-full">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-b from-blue-500/20 via-indigo-500/20 to-violet-500/20 rounded-2xl blur-lg" />

              <div className="relative bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-blue-500/30 p-5 h-full flex flex-col">
                {/* AI Header */}
                <div className="text-center mb-4">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    className="w-12 h-12 mx-auto mb-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30"
                  >
                    <BrainCircuit className="w-6 h-6 text-white" />
                  </motion.div>
                  <h3 className="text-sm font-bold text-white">AI Transformation Engine</h3>
                  <p className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider">Intelligent Mapping Layer</p>
                </div>

                {/* Transformation steps */}
                <div className="space-y-2 flex-1">
                  {[
                    { label: "Schema Analysis", desc: "Understanding source data structure", color: "blue" },
                    { label: "Field Mapping", desc: "Matching fields to Asha's schema", color: "indigo" },
                    { label: "Code Translation", desc: "Converting procedure & diagnosis codes", color: "violet" },
                    { label: "Relationship Linking", desc: "Preserving patient-record connections", color: "purple" },
                    { label: "Data Normalization", desc: "Standardizing formats & deduplication", color: "blue" },
                  ].map((step, i) => (
                    <motion.div
                      key={step.label}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className="relative"
                    >
                      <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 border border-white/5">
                        <motion.div
                          className="w-2 h-2 rounded-full bg-blue-400 shrink-0"
                          animate={{
                            boxShadow: [
                              "0 0 0 0 rgba(96,165,250,0.4)",
                              "0 0 0 6px rgba(96,165,250,0)",
                            ],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            delay: i * 0.3,
                          }}
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-white leading-tight">{step.label}</p>
                          <p className="text-[10px] text-slate-500 leading-tight truncate">{step.desc}</p>
                        </div>
                        <motion.div
                          className="ml-auto"
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }}
                        >
                          <Sparkles className="w-3 h-3 text-blue-400" />
                        </motion.div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Processing indicator */}
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="flex items-center gap-2 justify-center">
                    <motion.div
                      className="h-1 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500"
                      animate={{ width: ["20%", "100%", "20%"] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                      style={{ maxWidth: "100%" }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 text-center mt-1.5 font-medium">AI processing in real-time</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* FLOW ARROWS: AI → Asha */}
          <div className="w-[5%] flex flex-col items-center justify-center gap-2 relative">
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                className="flex items-center"
                animate={{ x: [0, 8, 0], opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  delay: i * 0.3 + 0.9,
                }}
              >
                <ArrowRight className="w-4 h-4 text-emerald-400/70" />
              </motion.div>
            ))}
          </div>

          {/* DESTINATION: Asha PMS */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="w-[28%] flex flex-col"
          >
            <div className="relative h-full">
              <div className="absolute -inset-0.5 bg-gradient-to-b from-emerald-500/20 to-blue-500/20 rounded-2xl blur-sm" />
              <div className="relative bg-slate-800/60 backdrop-blur-sm rounded-2xl border border-emerald-500/30 p-5 h-full flex flex-col">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center p-1.5">
                    <img
                      src="/assets/alogo.png"
                      alt="Asha"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Asha PMS</h3>
                    <p className="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold">Validated & Live</p>
                  </div>
                </div>

                {/* Incoming data with check animations */}
                <div className="space-y-1.5 flex-1">
                  {sourceDataItems.map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + i * 0.08 }}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 1.2 + i * 0.1, type: "spring" }}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      </motion.div>
                      <span className="text-xs font-medium text-slate-300">{item.label}</span>
                      <motion.span
                        className="ml-auto text-[9px] font-bold text-emerald-400 uppercase"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4 + i * 0.1 }}
                      >
                        Verified
                      </motion.span>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-emerald-500/20">
                  <div className="flex items-center gap-2 justify-center">
                    <Shield className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                      100% Data Integrity
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom stats */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="flex items-center justify-center gap-5 mt-4 flex-shrink-0 relative z-10"
      >
        {[
          { icon: Zap, text: "2–3 days typical migration", color: "text-blue-400" },
          { icon: Shield, text: "AI-validated for data integrity", color: "text-emerald-400" },
          { icon: Sparkles, text: "AI-powered validation", color: "text-violet-400" },
        ].map((stat, i) => (
          <div
            key={i}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/5"
          >
            <stat.icon className={`w-3.5 h-3.5 ${stat.color}`} />
            <span className="text-xs font-semibold text-slate-400">{stat.text}</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default DataMigrationSlide;
